import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, type OnChanges, type OnInit, type SimpleChanges } from '@angular/core';
import { FilialService } from '@zaoblako/web/core/services/finance/filial.service';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { StandardMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/standard-dropdown/standard-multi-select/standard-multi-select.component';
import { reconcileSelectionByNumericIds } from '@zaoblako/web/shared/helpers/dropdown-catalog-selection-reconcile.helper';
import type { FilialFindInterface } from '@zaoblako/shared/modules/catalog/dtos/filial/filial-find.dto';
import type { FiltersUiVersion } from '@zaoblako/web/core/types/filters-ui-version.type';

/**
 * @deprecated Use `crm-standard-multi-select` and `FilialService.dropdownDescriptorUrl`.
 */
@Component({
  selector: 'crm-filial-multi-select',
  standalone: true,
  imports: [MultiSelectComponent, StandardMultiSelectComponent],
  templateUrl: './filial-multi-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilialMultiSelectComponent implements OnInit, OnChanges {
  @Input() public filtersUiVersion: FiltersUiVersion = 'v2';
  @Input() public filials: FilialFindInterface[] = [];
  @Input() public selectedFilials: FilialFindInterface[] = [];
  @Output() public selectedFilialsChange = new EventEmitter<FilialFindInterface[]>();
  @Input() public customRequest = false;
  @Input() public sort: string;
  @Input() public class: string;
  @Input() public styleClass: string;
  @Input() public showClear = true;
  @Input() public disabled = false;
  @Input() public isError = false;
  @Input() public isFloatLabel = true;
  @Input() public placeholder = '\u00A0';
  @Input() public display = 'chip';
  @Input() public appendTo: string;

  /** Set only after `setSelectedFilialIds`. `null` means the filter input is not used. */
  public filialIds: number[] | null = null;
  public loading = false;
  public selectionLoading = false;
  public useStandard = false;
  public readonly descriptorUrl: string;
  private inputsReady = false;

  public constructor(
    filialService: FilialService,
    private readonly globalService: GlobalService
  ) {
    this.descriptorUrl = filialService.dropdownDescriptorUrl;
  }

  @Input() public set setSelectedFilialIds(ids: number[] | null | undefined) {
    this.filialIds = (ids ?? []).map((id) => (typeof id === 'string' ? Number(id) : id));
    if (this.inputsReady && !this.useStandard) {
      this.applyCustomSelection();
    }
  }

  public ngOnInit(): void {
    this.useStandard = !this.customRequest && !!this.globalService.globalInfo?.tenantInfo?.ratePlan?.modulesAccessControl?.BASIS?.enabled;
    this.inputsReady = true;
    if (!this.useStandard) {
      this.applyCustomSelection();
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!this.useStandard && changes.filials && !changes.filials.isFirstChange()) {
      this.applyCustomSelection();
    }
  }

  public onSelected = (filials: FilialFindInterface[]): void => {
    this.selectedFilials = filials ?? [];
    this.selectedFilialsChange.emit(this.selectedFilials);
  };

  private applyCustomSelection = (): void => {
    const ids = this.filialIds;
    if (!ids?.length) {
      this.selectionLoading = false;
      return;
    }

    const { selected, hasStaleKeys } = reconcileSelectionByNumericIds(this.filials, ids);
    const selectionDiffers =
      !this.selectedFilials?.length ||
      this.selectedFilials.length !== selected.length ||
      !this.selectedFilials.every((filial) => selected.some((item) => item.id === filial.id));

    if (!selectionDiffers && !hasStaleKeys) {
      this.selectionLoading = false;
      return;
    }

    this.selectedFilials = selected;
    this.selectionLoading = false;
    if (hasStaleKeys) {
      this.onSelected(selected);
    }
  };
}
